import React, { useEffect, useMemo, useRef, useState } from 'react';

import { ChaseSpinner } from '@components/Shared';

type Props<T> = {
  /** Array of items to be rendered in the list */
  data: T[];

  /** Function that returns a unique key for each item */
  keyExtractor: (item: T, index: number) => React.Key;

  /** Function that renders a single item */
  renderItem: (item: T, index: number) => React.ReactNode;

  /** Indicates whether there is more data to load */
  hasNext?: boolean;

  /** Number of items to load per batch (default: 50) */
  batchSize?: number;

  /** Whether the list is currently loading more items */
  isLoading?: boolean;

  /** Root margin for the IntersectionObserver (default: '100px') */
  rootMargin?: string;

  /** Text to display when the list is empty */
  emptyText?: string;

  /** Custom placeholder to show when the list is empty */
  emptyPlaceholder?: React.ReactNode;

  /** Optional class name for the list container */
  containerListClassName?: string;

  /** Custom loading placeholder (defaults to a spinner if not provided) */
  loadingPlaceholder?: React.ReactNode;

  /** Callback function to load the next batch of items */
  onNext?: () => void;

  /**
   * Optional render prop to customize how the list content
   * and empty placeholder are rendered
   */
  children?: (
    content: React.ReactNode[],
    emptyPlaceholder?: React.ReactNode,
  ) => React.ReactNode;
};

export const InfiniteScrollList = <T,>({
  data,
  hasNext,
  emptyText,
  isLoading,
  emptyPlaceholder,
  loadingPlaceholder,
  containerListClassName,
  onNext,
  children,
  renderItem,
  keyExtractor,
  batchSize = 50,
  rootMargin = '100px',
}: Props<T>) => {
  const bottomRef = useRef<HTMLDivElement | null>(null);

  const [visibleCount, setVisibleCount] = useState(batchSize);

  useEffect(() => {
    if (!bottomRef.current || isLoading) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          let newVisibleCount = batchSize;

          setVisibleCount((prev) => {
            newVisibleCount = Math.min(prev + batchSize, data.length);
            return newVisibleCount;
          });

          if (onNext && hasNext && newVisibleCount >= data.length) {
            onNext();
          }
        }
      },
      {
        rootMargin,
      },
    );

    if (bottomRef.current) {
      observer.observe(bottomRef.current);
    }

    return () => observer.disconnect();
  }, [data.length, hasNext, isLoading, onNext, batchSize, rootMargin]);

  const visibleItems = data.slice(0, visibleCount);

  const content = visibleItems.map((item, index) => (
    <React.Fragment key={keyExtractor(item, index)}>
      {renderItem(item, index)}
    </React.Fragment>
  ));

  const emptyPlaceholderContent = useMemo(() => {
    if (isLoading || visibleItems.length !== 0) {
      return;
    }

    if (emptyPlaceholder) {
      return emptyPlaceholder;
    }

    return emptyText ? (
      <p className="my-32 break-words text-center text-lg text-neural-04">
        {emptyText}
      </p>
    ) : null;
  }, [emptyPlaceholder, emptyText, isLoading, visibleItems.length]);

  return (
    <>
      {children?.(content, emptyPlaceholderContent) ?? (
        <div className={containerListClassName}>
          {content}
          {emptyPlaceholderContent}
        </div>
      )}

      <div ref={bottomRef} />

      {isLoading &&
        (loadingPlaceholder || (
          <div className="my-40 flex justify-center">
            <ChaseSpinner color="black" className="absolute h-14 w-14" />
          </div>
        ))}
    </>
  );
};
